import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { QRCodeComponent } from 'angularx-qrcode';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuthService } from 'src/app/seguranca/auth.service';
import { generatePixPayload } from 'src/app/shared/helper/pix-qrcode.util';
import { MensagemService } from 'src/app/shared/services/mensagem.service';
import { FaturaParcela, obterIdParcela } from '../pagamento-page/models/pagamento';
import { PagamentoService } from '../pagamento-page/services/pagamento.service';
import { FaturaViewModel } from './models/fatura.model';

type FaturaParcelaCache = FaturaParcela & { invoiceId?: string };

@Component({
  selector: 'app-pagamento-fatura-page',
  standalone: true,
  imports: [CommonModule, QRCodeComponent, ToastModule],
  providers: [MensagemService, MessageService],
  templateUrl: './pagamento-fatura-page.component.html',
  styleUrl: './pagamento-fatura-page.component.scss',
})
export class PagamentoFaturaPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private pagamentoService = inject(PagamentoService);
  private messageService = inject(MensagemService);

  formaPagamentoSelecionada: 'boleto' | 'pix' = 'boleto';
  carregando = false;

  fatura?: FaturaViewModel;
  pixCopiaECola?: string;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const cacheKey = this.route.snapshot.queryParamMap.get('cacheKey');

    if (cacheKey) {
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        const faturaParcela: FaturaParcelaCache = JSON.parse(cached);

        this.fatura = this.preencherFaturar(faturaParcela);

        localStorage.removeItem(cacheKey);
        return;
      }
    }

    if (id) {
      this.carregarFatura(id);
    }
  }

  carregarFatura(id: string): void {
    this.carregando = true;

    this.pagamentoService
      .buscarFaturarUso(this.authService.idempresa)
      .subscribe({
        next: (response) => {
          const parcela =
            response.parcelas?.find((p) => obterIdParcela(p) === id) ??
            response.parcelas?.[0];

          if (response.hasPendingPayment && parcela) {
            this.fatura = this.preencherFaturar({ invoiceId: obterIdParcela(parcela), ...parcela });
            this.carregando = false;
          } else {
            this.messageService.showWarn('Não existe fatura de uso');
          }
        },
        error: (error) => {
          this.messageService.showWarn('Erro ao buscar faturar');
          console.log(error);
        },
      });
  }

  selecionarFormaPagamento(forma: 'boleto' | 'pix'): void {
    this.formaPagamentoSelecionada = forma;
  }

  abrirBoleto(): void {
    if (!this.fatura?.boletoPdfUrl) {
      return;
    }

    window.open(this.fatura.boletoPdfUrl, '_blank', 'noopener,noreferrer');
  }

  imprimirBoleto(): void {
    if (!this.fatura?.boletoPdfUrl) {
      return;
    }

    const printWindow = window.open(
      this.fatura.boletoPdfUrl,
      '_blank',
      'noopener,noreferrer',
    );

    if (!printWindow) {
      return;
    }

    const tentarImprimir = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch {}
    };

    setTimeout(tentarImprimir, 1000);
  }

  copiarPix(): void {
    navigator.clipboard
      .writeText(this.fatura?.pixCopiaECola!)
      .then(() => {
        this.messageService.showInfo('Código Pix copiado com sucesso.');
      })
      .catch(() => {
        this.messageService.showWarn('Não foi possível copiar o código Pix.');
      });
  }

  private preencherFaturar(faturaUso: FaturaParcelaCache): FaturaViewModel {
    const empresaCnpjPix = '60818827000139';

    const pixCopiaECola = generatePixPayload({
      pixKey: empresaCnpjPix,
      merchantName: 'Chronosinfo Automação comerci',
      merchantCity: 'Itabaiana',
      amount: faturaUso.value,
      txid: faturaUso.invoiceId,
      description: 'Pagamento da fatura ' + faturaUso.invoiceId,
    });

    return {
      id: faturaUso.invoiceId!,
      valorTotal: faturaUso.value ?? 0,
      compradorEmail: '',
      compradorNome: this.authService.getEmpresa()?.nome ?? '',
      dataVencimento: faturaUso.dueDate!,
      empresaCnpjPix: empresaCnpjPix,
      empresaNome: 'Chronosinfo Automação comercial',
      numeroFatura: faturaUso.invoiceId!,
      status: 'PENDENTE',
      boletoPdfUrl: faturaUso.bankSlipUrl,
      descricao: 'Cobrança Recorrente do Sistema ERP',
      pixCopiaECola: pixCopiaECola,
    };
  }

  get statusDescricao(): string {
    switch (this.fatura?.status) {
      case 'PAGO':
        return 'Pagamento realizado';
      case 'VENCIDO':
        return 'Pagamento vencido';
      default:
        return 'Aguardando pagamento';
    }
  }

  get diasParaVencimento(): string {
    const hoje = new Date();
    const vencimento = new Date(this.fatura?.dataVencimento!);

    const diffMs = vencimento.getTime() - hoje.getTime();
    const diffDias = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDias > 0) {
      return `(daqui a ${diffDias} dias)`;
    }

    if (diffDias === 0) {
      return '(vence hoje)';
    }

    return `(vencido há ${Math.abs(diffDias)} dias)`;
  }
}
